'use client';

import { useCallback, useMemo, useState } from 'react';
import { setOpenLoginModal } from '@/stores';
import { useAuthMe } from '@/features/user';
import { useRouter } from 'next/navigation';
import { toast } from 'react-toastify';
import { useSendCv } from '@/features/jobs';
import { postMentorshipApply } from '@/features/mentorship';
import type { MentorshipProgramDetail } from '@/shared/job-mentorship';

/** GraphQL (Apollo), axios-ийн response зэргээс хэрэглэгчид харуулах мессеж. */
function getRegistrationSubmitErrorMessage(error: unknown): string | undefined {
   if (!error || typeof error !== 'object') return undefined;
   const gql = (
      error as { graphQLErrors?: readonly { message?: string }[] }
   ).graphQLErrors?.[0]?.message;
   if (gql) return gql;

   const ax = error as {
      response?: { data?: { message?: string } };
   };
   return ax.response?.data?.message;
}

type Params = {
  programId: number;
  jobId: number;
  canApply: boolean;
  questions: MentorshipProgramDetail['questions'];
};

export function useMentorshipRegistration({
  programId,
  jobId,
  canApply,
  questions,
}: Params) {
  const { data: user } = useAuthMe();
  const router = useRouter();
  const { mutateAsync: sendCv } = useSendCv();

  const [cvOpen, setCvOpen] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [thanksOpen, setThanksOpen] = useState(false);
  const [applyId, setApplyId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  const activeQuestions = useMemo(
    () => questions.filter((q) => q.active),
    [questions]
  );

  const hasRenderableSurvey = useMemo(
    () =>
      activeQuestions.some((q) =>
        ['yesno', 'option', 'multicheck', 'text', 'rate'].includes(q.qtype)
      ),
    [activeQuestions]
  );

  const onLogin = useCallback(() => {
    setCvOpen(false);
    setOpenLoginModal(true);
  }, []);

  const onEditCv = useCallback(() => {
    setCvOpen(false);
    router.push('/dashboard/my-cv');
  }, [router]);

  const onAfterCvSend = useCallback(async () => {
    if (!user?.id) return;
    setBusy(true);
    try {
      /* Эхлээд зар дээр CV илгээнэ — давхар илгээлт энд зогсоно, postMentorshipApply дутуу үүсэхгүй. */
      if (jobId > 0) {
        await sendCv(jobId);
      }
      const res = await postMentorshipApply(programId);
      const aid = res.data?.applyId;
      setApplyId(aid ?? null);
      setCvOpen(false);
      if (hasRenderableSurvey) {
        setSurveyOpen(true);
      } else {
        setThanksOpen(true);
      }
    } catch (e: unknown) {
      const msg = getRegistrationSubmitErrorMessage(e);
      toast.error(msg || 'Бүртгүүлэхэд алдаа гарлаа.');
    } finally {
      setBusy(false);
    }
  }, [user?.id, programId, jobId, hasRenderableSurvey, sendCv]);

  return {
    user,
    registrationAllowed: canApply,
    cvOpen,
    setCvOpen,
    surveyOpen,
    setSurveyOpen,
    thanksOpen,
    setThanksOpen,
    applyId,
    busy,
    activeQuestions,
    onLogin,
    onEditCv,
    onAfterCvSend,
  };
}
